import { useState, useEffect, useRef } from 'react';
import { PostItNote, PostItPosition } from './PostItNote';
import { AudioEngine, ButtonColor } from '../core/AudioEngine';
import './TutorialGame.css';

interface RhythmTutorialGameProps {
  onComplete: () => void;
  onSkip: () => void;
}

const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 500;

interface Button {
  id: number;
  x: number;
  y: number;
  color: string;
  audioColor: ButtonColor;
  health?: number; // For wear step
}

type RhythmStep = 'TIMING' | 'WEAR' | 'PANIC' | 'COMPLETE';

const BUTTON_COLORS = ['#FF6B6B', '#4ECDC4', '#FFD93D', '#A78BFA'];
const BUTTON_AUDIO_COLORS: ButtonColor[] = ['red', 'green', 'yellow', 'blue'];
const TIMING_PERFECT = 50;  // ±50ms
const TIMING_GOOD = 150;    // ±150ms

export const RhythmTutorialGame = ({ onComplete, onSkip }: RhythmTutorialGameProps) => {
  const [currentStep, setCurrentStep] = useState<RhythmStep>('TIMING');
  const [buttons, setButtons] = useState<Button[]>([]);
  const [highlightedButton, setHighlightedButton] = useState<number | null>(null);
  const [blinkingButton, setBlinkingButton] = useState<number | null>(null);
  const [isPlayingSequence, setIsPlayingSequence] = useState(false);
  const [audioEngine] = useState(() => new AudioEngine());
  const [postItMessage, setPostItMessage] = useState<string>('');
  const [showPostItNext, setShowPostItNext] = useState(false);
  const [postItPosition] = useState<PostItPosition>('top-center');
  const [userSequence, setUserSequence] = useState<number[]>([]);
  const [currentSequence, setCurrentSequence] = useState<number[]>([]);
  const [showCelebration, setShowCelebration] = useState(false);
  const [timingFeedback, setTimingFeedback] = useState<string | null>(null);
  const [expectedBeatTime, setExpectedBeatTime] = useState<number | null>(null);
  const [panicMode, setPanicMode] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Guided iteration tracking - 3 iterations per step
  const [guidedIterations, setGuidedIterations] = useState<Record<RhythmStep, number>>({
    'TIMING': 0,
    'WEAR': 0,
    'PANIC': 0,
    'COMPLETE': 0,
  });

  // Initialize audio and start first step
  useEffect(() => {
    audioEngine.init();
    startTimingStep();
  }, []);

  const startTimingStep = () => {
    setCurrentStep('TIMING');
    const isGuided = guidedIterations['TIMING'] < 3;
    
    if (isGuided) {
      setPostItMessage('En Rhythm, el **timing** importa. Toca cuando el botón brilla.');
    } else {
      setPostItMessage('');
    }
    
    setShowPostItNext(false);
    setShowCelebration(false);
    
    // Fixed sequence: [0, 2] (tempo 800ms)
    const sequence = [0, 2];
    setCurrentSequence(sequence);
    setUserSequence([]);
    
    initButtons();
    
    // Play sequence after short delay
    setTimeout(() => {
      playSequence(sequence, 800, isGuided);
    }, 800);
  };

  const startWearStep = () => {
    setCurrentStep('WEAR');
    const isGuided = guidedIterations['WEAR'] < 3;
    
    if (isGuided) {
      setPostItMessage('Los botones pierden vida y pueden cambiar de color. Fíjate bien.');
    } else {
      setPostItMessage('');
    }
    
    setShowPostItNext(false);
    setShowCelebration(false);
    
    // Fixed sequence: [1, 3]
    const sequence = [1, 3];
    setCurrentSequence(sequence);
    setUserSequence([]);
    
    initButtonsWithHealth();
    
    // Play sequence after short delay
    setTimeout(() => {
      playSequence(sequence, 800, isGuided);
    }, 800);
  };

  const startPanicStep = () => {
    setCurrentStep('PANIC');
    const isGuided = guidedIterations['PANIC'] < 3;
    
    if (isGuided) {
      setPostItMessage('Responde rápido y activa el **modo pánico** para multiplicar tu puntaje.');
    } else {
      setPostItMessage('');
    }
    
    setShowPostItNext(false);
    setShowCelebration(false);
    setPanicMode(false);
    
    // Fixed sequence: [0, 1, 2, 3] (tempo 500ms)
    const sequence = [0, 1, 2, 3];
    setCurrentSequence(sequence);
    setUserSequence([]);
    
    initButtons();
    
    // Play sequence after short delay
    setTimeout(() => {
      playSequence(sequence, 500, isGuided);
    }, 800);
  };

  const startCompleteStep = () => {
    setCurrentStep('COMPLETE');
    setPostItMessage('¡Listo para Rhythm!');
    setShowPostItNext(false);
    setShowCelebration(true);
    setButtons([]); // Clear board
  };

  const initButtons = () => {
    const positions = [
      { x: 200, y: 150 },
      { x: 400, y: 150 },
      { x: 200, y: 350 },
      { x: 400, y: 350 },
    ];

    const newButtons: Button[] = [];
    for (let i = 0; i < 4; i++) {
      newButtons.push({
        id: i,
        x: positions[i].x,
        y: positions[i].y,
        color: BUTTON_COLORS[i],
        audioColor: BUTTON_AUDIO_COLORS[i],
      });
    }

    setButtons(newButtons);
  };

  const initButtonsWithHealth = () => {
    const positions = [
      { x: 200, y: 150 },
      { x: 400, y: 150 },
      { x: 200, y: 350 },
      { x: 400, y: 350 },
    ];

    const newButtons: Button[] = [];
    for (let i = 0; i < 4; i++) {
      newButtons.push({
        id: i,
        x: positions[i].x,
        y: positions[i].y,
        color: BUTTON_COLORS[i],
        audioColor: BUTTON_AUDIO_COLORS[i],
        health: 100, // Full health
      });
    }

    setButtons(newButtons);
  };

  const playSequence = async (sequence: number[], tempo: number, isGuided: boolean = true) => {
    setIsPlayingSequence(true);
    setUserSequence([]);
    
    for (const buttonId of sequence) {
      const button = buttons.find(b => b.id === buttonId);
      if (button) {
        setHighlightedButton(buttonId);
        audioEngine.playTone(button.audioColor, button.x, CANVAS_WIDTH, tempo);
        await new Promise(resolve => setTimeout(resolve, tempo));
        setHighlightedButton(null);
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
    
    setIsPlayingSequence(false);
    
    // Start blinking first correct button only if guided
    if (isGuided) {
      setBlinkingButton(sequence[0]);
      if (currentStep === 'TIMING') {
        setExpectedBeatTime(Date.now() + tempo);
      }
    }
  };

  const handleButtonClick = (buttonId: number) => {
    if (isPlayingSequence || showCelebration) return;

    const button = buttons.find(b => b.id === buttonId);
    if (!button) return;

    const newUserSequence = [...userSequence, buttonId];
    setUserSequence(newUserSequence);
    
    // Check current guided state once
    const isGuided = guidedIterations[currentStep] < 3;

    // Check if correct so far
    const expectedIndex = newUserSequence.length - 1;
    if (currentSequence[expectedIndex] !== buttonId) {
      // Wrong button
      audioEngine.playTone('red', 300, CANVAS_WIDTH, 300);
      setTimingFeedback(null);
      const isGuided = guidedIterations[currentStep] < 3;
      setTimeout(() => {
        const tempo = currentStep === 'PANIC' ? 500 : 800;
        playSequence(currentSequence, tempo, isGuided);
      }, 800);
      return;
    }

    // Timing check (TIMING step)
    if (currentStep === 'TIMING' && expectedBeatTime) {
      const now = Date.now();
      const timingError = Math.abs(now - expectedBeatTime);
      
      if (timingError <= TIMING_PERFECT) {
        setTimingFeedback('¡Perfecto! 🎯');
      } else if (timingError <= TIMING_GOOD) {
        setTimingFeedback('¡Bien! ✅');
      } else {
        setTimingFeedback('Fuera de tiempo ⏱️');
      }

      setTimeout(() => setTimingFeedback(null), 1000);
    }

    // Play button sound
    audioEngine.playTone(button.audioColor, button.x, CANVAS_WIDTH, 300);
    setHighlightedButton(buttonId);
    setTimeout(() => setHighlightedButton(null), 300);

    // Wear step: reduce health and potentially change button
    if (currentStep === 'WEAR' && newUserSequence.length === 1) {
      // After first correct hit, trigger wear
      setTimeout(() => {
        setButtons(prevButtons => {
          const newButtons = [...prevButtons];
          const wornButton = newButtons[buttonId];
          wornButton.health = 50; // Reduce health
          wornButton.color = BUTTON_COLORS[(buttonId + 1) % 4]; // Change color
          wornButton.audioColor = BUTTON_AUDIO_COLORS[(buttonId + 1) % 4]; // Change sound
          return newButtons;
        });
        
        // Play new sequence including worn button
        const newSeq = [buttonId, currentSequence[1]];
        setCurrentSequence(newSeq);
        setTimeout(() => {
          playSequence(newSeq, 800, isGuided);
        }, 500);
      }, 1000);
      return;
    }

    // Panic step: check speed and activate panic mode
    if (currentStep === 'PANIC' && !panicMode) {
      // Simple panic activation: if completing sequence quickly
      if (newUserSequence.length === currentSequence.length) {
        setPanicMode(true);
        setTimingFeedback('¡Pánico activado! x2 🔥');
        setTimeout(() => setTimingFeedback(null), 1500);
      }
    }

    // Update blink to next button (only if guided)
    if (newUserSequence.length < currentSequence.length) {
      if (isGuided) {
        setBlinkingButton(currentSequence[newUserSequence.length]);
        if (currentStep === 'TIMING') {
          const tempo = 800;
          setExpectedBeatTime(Date.now() + tempo);
        }
      }
    } else {
      // Sequence complete!
      setBlinkingButton(null);
      
      // Increment guided iteration counter
      const newCount = guidedIterations[currentStep] + 1;
      setGuidedIterations(prev => ({ ...prev, [currentStep]: newCount }));
      
      // If still guided, show celebration and replay
      if (newCount < 3) {
        setShowCelebration(true);
        
        if (currentStep === 'TIMING') {
          setPostItMessage(`¡Excelente timing! 🎵 (${newCount}/3)`);
        } else if (currentStep === 'WEAR') {
          setPostItMessage(`¡Adaptaste al cambio! 🔄 (${newCount}/3)`);
        } else if (currentStep === 'PANIC') {
          setPostItMessage(`¡Dominaste el pánico! ⚡ (${newCount}/3)`);
        }
        
        // Auto-restart after short delay
        setTimeout(() => {
          if (currentStep === 'TIMING') {
            startTimingStep();
          } else if (currentStep === 'WEAR') {
            startWearStep();
          } else if (currentStep === 'PANIC') {
            startPanicStep();
          }
        }, 1500);
      } else {
        // After 3 iterations, allow advancing to next step
        setShowCelebration(true);
        
        if (currentStep === 'TIMING') {
          setPostItMessage('¡Excelente timing! 🎵');
        } else if (currentStep === 'WEAR') {
          setPostItMessage('¡Adaptaste al cambio! 🔄');
        } else if (currentStep === 'PANIC') {
          setPostItMessage('¡Dominaste el pánico! ⚡');
        }
        
        setShowPostItNext(true);
      }
    }
  };

  const handleNext = () => {
    if (currentStep === 'TIMING') {
      startWearStep();
    } else if (currentStep === 'WEAR') {
      startPanicStep();
    } else if (currentStep === 'PANIC') {
      startCompleteStep();
    }
  };

  const handleSkip = () => {
    onSkip();
  };

  // Draw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Panic mode background effect
    if (panicMode && currentStep === 'PANIC') {
      ctx.fillStyle = 'rgba(255, 100, 100, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    buttons.forEach((button) => {
      const isHighlighted = highlightedButton === button.id;
      const isBlinking = blinkingButton === button.id;

      ctx.beginPath();
      ctx.arc(button.x, button.y, 50, 0, Math.PI * 2);
      
      // Adjust color based on health (WEAR step)
      let fillColor = button.color;
      if (currentStep === 'WEAR' && button.health && button.health < 100) {
        fillColor = button.color; // Already changed in state
      }
      
      ctx.fillStyle = isHighlighted ? '#FFFFFF' : fillColor;
      ctx.strokeStyle = isBlinking ? '#FFD700' : '#000000'; // Golden blink
      ctx.lineWidth = isBlinking ? 6 : 2;
      
      ctx.fill();
      ctx.stroke();

      // Health bar for WEAR step
      if (currentStep === 'WEAR' && button.health !== undefined) {
        const barWidth = 60;
        const barHeight = 6;
        const barX = button.x - barWidth / 2;
        const barY = button.y + 60;

        // Background
        ctx.fillStyle = '#333';
        ctx.fillRect(barX, barY, barWidth, barHeight);

        // Health
        ctx.fillStyle = button.health > 50 ? '#4ECDC4' : '#FF6B6B';
        ctx.fillRect(barX, barY, (barWidth * button.health) / 100, barHeight);
      }
    });

    // Timing feedback overlay
    if (timingFeedback) {
      ctx.font = 'bold 24px Arial';
      ctx.textAlign = 'center';
      
      if (timingFeedback.includes('Perfecto')) {
        ctx.fillStyle = '#00FF00';
      } else if (timingFeedback.includes('Bien')) {
        ctx.fillStyle = '#FFD700';
      } else if (timingFeedback.includes('Fuera')) {
        ctx.fillStyle = '#FF0000';
      } else {
        ctx.fillStyle = '#FF6B6B';
      }
      
      ctx.fillText(timingFeedback, CANVAS_WIDTH / 2, 50);
    }

    // Panic indicator
    if (currentStep === 'PANIC' && !panicMode) {
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#666';
      ctx.fillText('Responde rápido para activar pánico', CANVAS_WIDTH / 2, CANVAS_HEIGHT - 20);
    }
  }, [buttons, highlightedButton, blinkingButton, currentStep, timingFeedback, panicMode]);

  // Blink animation
  useEffect(() => {
    if (blinkingButton === null) return;

    const interval = setInterval(() => {
      setBlinkingButton((prev) => (prev !== null ? null : currentSequence[userSequence.length]));
    }, 500);

    return () => clearInterval(interval);
  }, [blinkingButton, userSequence.length, currentSequence]);

  // Update expected beat time for timing blink sync
  useEffect(() => {
    if (currentStep !== 'TIMING' || blinkingButton === null || isPlayingSequence) return;

    const interval = setInterval(() => {
      const tempo = 800;
      setExpectedBeatTime(Date.now() + tempo);
    }, 800);

    return () => clearInterval(interval);
  }, [currentStep, blinkingButton, isPlayingSequence]);

  return (
    <div className="tutorial-game">
      <div className="tutorial-header">
        <h1>Tutorial Rhythm</h1>
        {currentStep !== 'COMPLETE' && (
          <button className="skip-button" onClick={handleSkip}>
            Saltar Tutorial
          </button>
        )}
      </div>

      {currentStep === 'COMPLETE' ? (
        <div className="tutorial-complete">
          <PostItNote
            message={postItMessage}
            position="top-center"
            showNextButton={false}
            onNext={() => {}}
          />
          <div className="tutorial-summary">
            <h2>Aprendiste:</h2>
            <ul>
              <li>✅ Timing preciso ±150ms</li>
              <li>✅ Botones pierden vida y cambian</li>
              <li>✅ Respuestas rápidas → modo pánico</li>
            </ul>
            <div className="tutorial-actions">
              <button className="primary-button" onClick={onComplete}>
                ¡Jugar Rhythm!
              </button>
              <button className="secondary-button" onClick={onSkip}>
                Cambiar Modo
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {postItMessage && (
            <PostItNote
              message={postItMessage}
              position={postItPosition}
              showNextButton={showPostItNext}
              onNext={handleNext}
            />
          )}

          <canvas
            ref={canvasRef}
            width={600}
            height={500}
            className="game-canvas"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const y = e.clientY - rect.top;

              buttons.forEach((button) => {
                const distance = Math.sqrt((x - button.x) ** 2 + (y - button.y) ** 2);
                if (distance <= 50) {
                  handleButtonClick(button.id);
                }
              });
            }}
            style={{ cursor: 'pointer' }}
          />

          {showCelebration && (
            <div className="celebration">
              <span className="celebration-emoji">🎉</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};
